import { InjectionToken, Provider } from '@angular/core';
import { PROJECT_DETAIL_FILTERS } from '@icilalune/detail-feature/tokens';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  switchMap,
} from 'rxjs';
import {
  BusinessUnits,
  contextApps,
  contextMotionDesign,
  FilterGroup,
  Project,
} from '@nx-icilalune/data/icilalune/types';
import { DrupalTerm } from '@nx-icilalune/drupal-types';
import { ROUTE_PROJECT_ID } from '@icilalune/router';
import { GetEntityPipe } from '@nx-icilalune/data/access';
import {
  TERMS_BUSINESS_UNIT,
  TERMS_DOMAIN,
  TERMS_TECHNOLOGY,
  TERMS_TYPE,
} from '@nx-icilalune/data/icilalune/tokens';

const ROUTED_PROJECT = new InjectionToken<Project>('ROUTED_PROJECT');
const PROJECT_BUSINESS_UNIT = new InjectionToken<BusinessUnits | undefined>(
  'PROJECT_BUSINESS_UNIT'
);

export function getProjectFilters(
  projectBusinessUnit$: Observable<BusinessUnits>,
  typeTerms$: Observable<DrupalTerm[]>,
  domainTerms$: Observable<DrupalTerm[]>,
  isList: boolean = false
) {
  return combineLatest([projectBusinessUnit$, typeTerms$, domainTerms$]).pipe(
    filter(
      ([projectBusinessUnit, typeTerms, domainTerms]) => !!projectBusinessUnit
    ),
    map(([projectBusinessUnit, typeTerms, domainTerms]) => {
      const filterGroups: FilterGroup[] = [];
      if (projectBusinessUnit === contextApps) {
        filterGroups.push({
          label: 'Domaines',
          filters: domainTerms,
          id: 'domains',
        });
        filterGroups.push({
          label: 'Types',
          filters: typeTerms,
          id: 'types',
        });
      } else if (projectBusinessUnit === contextMotionDesign) {
        filterGroups.push({
          label: 'Domaines',
          filters: domainTerms,
          id: 'domains',
        });
      }
      return filterGroups;
    })
  );
}

export const PROJECT_PROVIDERS: Provider[] = [
  {
    provide: ROUTED_PROJECT,
    deps: [ROUTE_PROJECT_ID, GetEntityPipe],
    useFactory: (
      projectId$: Observable<string>,
      getEntityPipe: GetEntityPipe
    ) => {
      return projectId$.pipe(
        filter((projectId) => !!projectId),
        distinctUntilChanged(),
        switchMap((projectId) => {
          return getEntityPipe.transform<Project>(projectId);
        })
      );
    },
  },
  {
    provide: PROJECT_BUSINESS_UNIT,
    deps: [ROUTED_PROJECT],
    useFactory: (project$: Observable<Project>) => {
      return project$.pipe(
        filter((project) => !!project),
        map((project) => {
          if (project?.businessUnitLabel?.toLowerCase() === 'apps') {
            return contextApps;
          } else if (
            project?.businessUnitLabel?.toLowerCase() === 'motion design'
          ) {
            return contextMotionDesign;
          }
          return undefined;
        }),
        distinctUntilChanged()
      );
    },
  },
  {
    provide: PROJECT_DETAIL_FILTERS,
    deps: [
      PROJECT_BUSINESS_UNIT,
      TERMS_TYPE,
      TERMS_DOMAIN,
      TERMS_BUSINESS_UNIT,
      TERMS_TECHNOLOGY,
    ],
    useFactory: (
      projectBusinessUnit$: Observable<BusinessUnits>,
      typeTerms$: Observable<DrupalTerm[]>,
      domainTerms$: Observable<DrupalTerm[]>
    ) => getProjectFilters(projectBusinessUnit$, typeTerms$, domainTerms$),
  },
];
